import * as React from "react";
import { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import { Loader } from "@/components/ui/loader";

import { GridSidebarSearchInput } from "./input";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { GridSidebarSearchResults } from "./results";
import { motion } from "framer-motion";
import { GridSidebarSearchWebsocket } from "./websocket";
import { useSearchBusinessReportsOutcomeQuery } from "@/modules/snapshots/businessReports.api";
import { ulid } from "ulid";
import { useSearchProgress } from "./useSearchProgress";
import { GridSidebarSearchTaskProgress } from "./taskProgress";

interface Props {
  selectedCollection: SnapshotCollection;
}

export const GridSidebarSearch: React.FC<Props> = ({ selectedCollection }) => {
  const [searchId, setSearchId] = React.useState(() => ulid());
  const [query, setQuery] = React.useState<string>("");

  const [thinking, setThinking] = React.useState<string>("");
  const [isThinkingComplete, setIsThinkingComplete] = React.useState<boolean>(false);
  const [documents, setDocuments] = React.useState<number>(0);
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);
  const [relevantFields, setRelevantFields] = React.useState<string[]>([]);

  const [snapshotCollectionOutcomes, setSnapshotCollectionOutcomes] = React.useState<
    BusinessReportOutcome[] | undefined
  >(undefined);

  // Track search progress with metrics
  const progressMetrics = useSearchProgress({
    query,
    isThinkingComplete,
    documents,
    isProcessing,
    snapshotCollectionOutcomes,
  });

  useSearchBusinessReportsOutcomeQuery(
    {
      business_report_id: selectedCollection.business_report_id,
      snapshot_collection_id: selectedCollection.id,
      query: query,
      search_id: searchId,
    },
    {
      skip: !query || !searchId,
      refetchOnFocus: false,
    },
  );

  const handleSearch = async (_searchId: string, query: string) => {
    setQuery(query);
    setSearchId(_searchId);
    setIsProcessing(true);
  };

  const handleReset = () => {
    setQuery("");
    setThinking("");
    setSearchId("");
    setDocuments(0);
    setIsThinkingComplete(false);
    setSnapshotCollectionOutcomes(undefined);
    setIsProcessing(false);
    setRelevantFields([]);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
      <GridSidebarSearchInput handleSearch={handleSearch} query={query} reset={handleReset} />
      {searchId && (
        <GridSidebarSearchWebsocket
          setDocuments={setDocuments}
          key={searchId}
          setSnapshotCollectionOutcomes={setSnapshotCollectionOutcomes}
          setIsProcessing={setIsProcessing}
          searchId={searchId}
          setThinking={setThinking}
          setIsThinkingComplete={setIsThinkingComplete}
          setRelevantFields={setRelevantFields}
        />
      )}

      {/* Task Progress Checklist */}
      {query && progressMetrics.phase && (
        <GridSidebarSearchTaskProgress
          metrics={progressMetrics}
          thinking={thinking}
          relevantFields={relevantFields}
          documents={documents}
        />
      )}

      {/* Results Section - Show progressively as results arrive */}
      {snapshotCollectionOutcomes && snapshotCollectionOutcomes.length > 0 && (
        <>
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <GridSidebarSearchResults snapshotCollectionOutcomes={snapshotCollectionOutcomes} />
          </motion.div>

          {/* Loading More Indicator */}
          {isProcessing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 text-center text-sm text-gray-500"
            >
              <Loader />
              <p className="mt-2">Loading more results... ({snapshotCollectionOutcomes?.length} found)</p>
            </motion.div>
          )}
        </>
      )}
      {/* Empty State */}
      {query && progressMetrics.phase === "complete" && !snapshotCollectionOutcomes?.length && (
        <div className="text-center text-sm text-gray-500">No snapshots found</div>
      )}
    </div>
  );
};
